/**
 * An SOP's promotion verdict per scenario type. Derived by the backend from each type's latest finished
 * bulk run on every read, so it is a query and never a stored copy: it moves when a bulk run settles
 * or the protocol is recompiled, and the former invalidates it from the run screen.
 */

import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { queryClient } from '@/lib/query-client';
import { aiFunctionService } from '@/services/ai-function.service';
import type { TypeReadiness } from '@/modules/back-testing/readiness/types';

export const typeReadinessKey = (sopId: string): string[] => ['back-testing', sopId, 'readiness'];

const typeReadinessQueryAtom = (sopId: string) =>
	atomWithQuery(() => ({
		queryKey: typeReadinessKey(sopId),
		queryFn: async (): Promise<Record<string, TypeReadiness>> => {
			const rows = await aiFunctionService.fetchReadiness(sopId);
			return Object.fromEntries(rows.map((row) => [row.typeId, row]));
		},
		enabled: Boolean(sopId),
		staleTime: 60 * 1000
	}));

/** The SOP's verdicts keyed by type id; a type with no entry has not been read yet. */
export function useTypeReadiness(sopId: string) {
	const queryAtom = useMemo(() => typeReadinessQueryAtom(sopId), [sopId]);
	const [query] = useAtom(queryAtom);
	return query;
}

/** Reads the verdicts again; a bulk run has just settled, so the one on screen may have moved. */
export const reloadTypeReadiness = (sopId: string): Promise<void> =>
	queryClient.invalidateQueries({ queryKey: typeReadinessKey(sopId) }).then(() => undefined);
